// Ingredient Conflict & Synergy Maps for K-Beauty Recommendations
// Based on dermatologist guidelines and skincare science

// ============================================================================
// Ingredient Conflicts
// These ingredients should NOT be used together in the same routine (AM or PM)
// ============================================================================

export const INGREDIENT_CONFLICTS: Record<string, string[]> = {
  // Retinoids conflict with acids and oxidizing agents
  Retinol: ['Benzoyl Peroxide', 'Vitamin C', 'AHA', 'BHA', 'Glycolic Acid', 'Salicylic Acid', 'Lactic Acid'],
  Retinoids: ['Benzoyl Peroxide', 'Vitamin C', 'AHA', 'BHA'],
  Tretinoin: ['Benzoyl Peroxide', 'Vitamin C', 'AHA', 'BHA'],

  // Vitamin C is sensitive to pH and oxidation
  'Vitamin C': ['Benzoyl Peroxide', 'AHA', 'BHA', 'Retinol', 'Niacinamide'], // Note: Niacinamide conflict is debated but we're being cautious

  // Acids don't mix well together (over-exfoliation risk)
  AHA: ['BHA', 'Retinol', 'Vitamin C'],
  BHA: ['AHA', 'Retinol', 'Vitamin C'],
  'Glycolic Acid': ['Salicylic Acid', 'Retinol', 'Lactic Acid'],
  'Salicylic Acid': ['Glycolic Acid', 'Retinol', 'Lactic Acid', 'Benzoyl Peroxide'],
  'Lactic Acid': ['Glycolic Acid', 'Salicylic Acid', 'Retinol'],

  // Benzoyl Peroxide oxidizes many actives
  'Benzoyl Peroxide': ['Retinol', 'Vitamin C', 'AHA', 'BHA', 'Hydroquinone'],

  // Copper Peptides have specific conflicts
  'Copper Peptide': ['Vitamin C', 'AHA', 'BHA', 'Retinol'],
}

// ============================================================================
// Ingredient Synergies
// These ingredients work BETTER together (boost each other's efficacy)
// ============================================================================

export const INGREDIENT_SYNERGIES: Record<string, { ingredients: string[]; bonus: number; reason: string }> = {
  // Niacinamide plays well with hydrators
  Niacinamide: {
    ingredients: ['Hyaluronic Acid', 'Ceramide', 'Peptide', 'Zinc', 'Panthenol'],
    bonus: 1.2,
    reason: 'Niacinamide boosts hydration efficacy',
  },

  // Hyaluronic Acid + occlusives = maximum hydration
  'Hyaluronic Acid': {
    ingredients: ['Ceramide', 'Glycerin', 'Panthenol', 'Squalane', 'Shea Butter'],
    bonus: 1.3,
    reason: 'HA attracts moisture, occlusives lock it in',
  },

  // Ceramides work with skin-identical lipids
  Ceramide: {
    ingredients: ['Cholesterol', 'Fatty Acids', 'Hyaluronic Acid', 'Niacinamide', 'Panthenol'],
    bonus: 1.25,
    reason: 'Ceramides restore barrier with complementary lipids',
  },

  // Famous CE Ferulic combination (SkinCeuticals patent)
  'Vitamin C': {
    ingredients: ['Vitamin E', 'Ferulic Acid'],
    bonus: 1.5,
    reason: 'CE Ferulic combo increases Vitamin C stability and efficacy by 8x',
  },

  // Retinol evening power combo
  Retinol: {
    ingredients: ['Peptide', 'Niacinamide', 'Ceramide', 'Hyaluronic Acid'],
    bonus: 1.2,
    reason: 'Hydrators buffer retinol irritation while maintaining efficacy',
  },

  // Centella/Cica soothing synergies
  Centella: {
    ingredients: ['Panthenol', 'Madecassoside', 'Niacinamide', 'Aloe', 'Beta-Glucan'],
    bonus: 1.2,
    reason: 'Soothing ingredients amplify calming effects',
  },
  'Centella Asiatica': {
    ingredients: ['Panthenol', 'Madecassoside', 'Niacinamide', 'Aloe', 'Beta-Glucan'],
    bonus: 1.2,
    reason: 'Soothing ingredients amplify calming effects',
  },

  // Snail Mucin repair combo
  'Snail Mucin': {
    ingredients: ['Centella', 'Niacinamide', 'Hyaluronic Acid', 'Panthenol'],
    bonus: 1.15,
    reason: 'Healing ingredients work synergistically for repair',
  },
}

// ============================================================================
// Time-Appropriate Actives
// Some ingredients should only be used AM or PM
// ============================================================================

export const TIME_PREFERENCES: {
  AM: { preferred: string[]; avoid: string[] }
  PM: { preferred: string[]; avoid: string[] }
} = {
  AM: {
    // Antioxidants protect during the day
    preferred: [
      'Vitamin C',
      'Niacinamide',
      'Hyaluronic Acid',
      'Green Tea',
      'Vitamin E',
      'Ferulic Acid',
      'Centella',
      'Aloe',
    ],
    // Photosensitizing ingredients should be PM only
    avoid: ['Retinol', 'Retinoids', 'Tretinoin', 'AHA', 'BHA', 'Glycolic Acid', 'Lactic Acid'],
  },
  PM: {
    // Cell turnover and repair happens at night
    preferred: [
      'Retinol',
      'Retinoids',
      'AHA',
      'BHA',
      'Glycolic Acid',
      'Salicylic Acid',
      'Peptide',
      'Ceramide',
      'Niacinamide',
      'Hyaluronic Acid',
    ],
    // Sunscreen ingredients have no benefit at night (but no harm either)
    avoid: [], // Nothing strictly avoided at PM
  },
}

// ============================================================================
// Active Ingredient Classes (for limiting per routine)
// ============================================================================

export const ACTIVE_CLASSES: Record<string, string[]> = {
  retinoid: ['Retinol', 'Retinoids', 'Tretinoin', 'Adapalene', 'Retinaldehyde'],
  'vitamin-c': ['Vitamin C', 'Ascorbic Acid', 'L-Ascorbic Acid', 'Sodium Ascorbyl Phosphate'],
  aha: ['AHA', 'Glycolic Acid', 'Lactic Acid', 'Mandelic Acid', 'Malic Acid'],
  bha: ['BHA', 'Salicylic Acid', 'Betaine Salicylate'],
  pha: ['PHA', 'Gluconolactone', 'Lactobionic Acid'],
  niacinamide: ['Niacinamide', 'Nicotinamide', 'Vitamin B3'],
  peptide: ['Peptide', 'Copper Peptide', 'Argireline', 'Matrixyl'],
  'benzoyl-peroxide': ['Benzoyl Peroxide'],
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalize ingredient name for matching
 * Removes hyphens, underscores, spaces and lowercases
 * "Plant-derived Surfactants" → "plantderivedsurfactants"
 */
export function normalizeIngredient(name: string): string {
  return name.toLowerCase().replace(/[-_\s]+/g, '').trim()
}

// ============================================================================
// Pre-normalized Maps (computed once at module load)
// ============================================================================

// Pre-normalize conflict keys and values
const NORMALIZED_CONFLICTS: Array<{ keyNorm: string; conflictsNorm: string[] }> = Object.entries(
  INGREDIENT_CONFLICTS
).map(([key, conflicts]) => ({
  keyNorm: normalizeIngredient(key),
  conflictsNorm: conflicts.map(normalizeIngredient),
}))

// Pre-normalize synergy keys and values
const NORMALIZED_SYNERGIES: Array<{
  keyNorm: string
  ingredientsNorm: string[]
  bonus: number
}> = Object.entries(INGREDIENT_SYNERGIES).map(([key, synergy]) => ({
  keyNorm: normalizeIngredient(key),
  ingredientsNorm: synergy.ingredients.map(normalizeIngredient),
  bonus: synergy.bonus,
}))

// Pre-normalize time preferences
const NORMALIZED_TIME_PREFS = {
  AM: {
    preferredNorm: TIME_PREFERENCES.AM.preferred.map(normalizeIngredient),
    avoidNorm: TIME_PREFERENCES.AM.avoid.map(normalizeIngredient),
  },
  PM: {
    preferredNorm: TIME_PREFERENCES.PM.preferred.map(normalizeIngredient),
    avoidNorm: TIME_PREFERENCES.PM.avoid.map(normalizeIngredient),
  },
}

// Pre-normalize active classes
const NORMALIZED_ACTIVE_CLASSES: Array<{ className: string; ingredientsNorm: string[] }> =
  Object.entries(ACTIVE_CLASSES).map(([className, ingredients]) => ({
    className,
    ingredientsNorm: ingredients.map(normalizeIngredient),
  }))

// ============================================================================
// Matching Functions (using pre-normalized maps)
// ============================================================================

/**
 * Check if two ingredients conflict
 */
export function hasConflict(ingredient1: string, ingredient2: string): boolean {
  const ing1Norm = normalizeIngredient(ingredient1)
  const ing2Norm = normalizeIngredient(ingredient2)

  for (const { keyNorm, conflictsNorm } of NORMALIZED_CONFLICTS) {
    // Check if ingredient1 is the key and ingredient2 is in conflicts
    if (ing1Norm.includes(keyNorm) && conflictsNorm.some((c) => ing2Norm.includes(c))) {
      return true
    }
    // Check reverse
    if (ing2Norm.includes(keyNorm) && conflictsNorm.some((c) => ing1Norm.includes(c))) {
      return true
    }
  }

  return false
}

/**
 * Check if any ingredients in a list conflict with each other
 */
export function hasAnyConflict(ingredients: string[]): boolean {
  for (let i = 0; i < ingredients.length; i++) {
    for (let j = i + 1; j < ingredients.length; j++) {
      if (hasConflict(ingredients[i], ingredients[j])) {
        return true
      }
    }
  }
  return false
}

/**
 * Get synergy bonus for an ingredient with already-selected ingredients
 */
export function getSynergyBonus(ingredient: string, selectedIngredients: string[]): number {
  const ingNorm = normalizeIngredient(ingredient)
  const selectedNorm = selectedIngredients.map(normalizeIngredient)

  for (const { keyNorm, ingredientsNorm, bonus } of NORMALIZED_SYNERGIES) {
    if (ingNorm.includes(keyNorm)) {
      // Check if any synergistic ingredients are already selected
      for (const synergyNorm of ingredientsNorm) {
        if (selectedNorm.some((sel) => sel.includes(synergyNorm))) {
          return bonus
        }
      }
    }
  }

  return 1.0 // No synergy bonus
}

/**
 * Check if ingredient is time-appropriate
 */
export function isTimeAppropriate(ingredient: string, time: 'AM' | 'PM'): { appropriate: boolean; penalty: number } {
  const ingNorm = normalizeIngredient(ingredient)
  const prefs = NORMALIZED_TIME_PREFS[time]

  // Check if it should be avoided at this time
  for (const avoidNorm of prefs.avoidNorm) {
    if (ingNorm.includes(avoidNorm)) {
      return { appropriate: false, penalty: 3.0 } // Heavy penalty for wrong time
    }
  }

  // Check if it's preferred at this time (bonus)
  for (const preferredNorm of prefs.preferredNorm) {
    if (ingNorm.includes(preferredNorm)) {
      return { appropriate: true, penalty: -0.5 } // Small bonus for right time
    }
  }

  return { appropriate: true, penalty: 0 } // Neutral
}

/**
 * Get the active class for an ingredient (for limiting actives per routine)
 */
export function getActiveClass(ingredient: string): string | null {
  const ingNorm = normalizeIngredient(ingredient)

  for (const { className, ingredientsNorm } of NORMALIZED_ACTIVE_CLASSES) {
    if (ingredientsNorm.some((i) => ingNorm.includes(i))) {
      return className
    }
  }

  return null
}

/**
 * Count number of actives in a list of products
 */
export function countActiveClasses(allIngredients: string[]): Set<string> {
  const classes = new Set<string>()

  for (const ing of allIngredients) {
    const activeClass = getActiveClass(ing)
    if (activeClass) {
      classes.add(activeClass)
    }
  }

  return classes
}

/**
 * Normalize a list of ingredients (for batch processing)
 */
export function normalizeIngredientList(ingredients: string[]): string[] {
  return ingredients.map(normalizeIngredient)
}

/**
 * Check conflict using pre-normalized ingredients
 * Use this when you've already normalized the ingredient lists
 */
export function hasConflictNormalized(ing1Norm: string, ing2Norm: string): boolean {
  for (const { keyNorm, conflictsNorm } of NORMALIZED_CONFLICTS) {
    if (ing1Norm.includes(keyNorm) && conflictsNorm.some((c) => ing2Norm.includes(c))) {
      return true
    }
    if (ing2Norm.includes(keyNorm) && conflictsNorm.some((c) => ing1Norm.includes(c))) {
      return true
    }
  }
  return false
}

/**
 * Check if any ingredients conflict (using pre-normalized list)
 */
export function hasAnyConflictNormalized(ingredientsNorm: string[]): boolean {
  for (let i = 0; i < ingredientsNorm.length; i++) {
    for (let j = i + 1; j < ingredientsNorm.length; j++) {
      if (hasConflictNormalized(ingredientsNorm[i], ingredientsNorm[j])) {
        return true
      }
    }
  }
  return false
}

/**
 * Get synergy bonus using pre-normalized ingredients
 */
export function getSynergyBonusNormalized(ingNorm: string, selectedNorm: string[]): number {
  for (const { keyNorm, ingredientsNorm, bonus } of NORMALIZED_SYNERGIES) {
    if (ingNorm.includes(keyNorm)) {
      for (const synergyNorm of ingredientsNorm) {
        if (selectedNorm.some((sel) => sel.includes(synergyNorm))) {
          return bonus
        }
      }
    }
  }
  return 1.0
}

/**
 * Check time-appropriate using pre-normalized ingredient
 */
export function isTimeAppropriateNormalized(
  ingNorm: string,
  time: 'AM' | 'PM'
): { appropriate: boolean; penalty: number } {
  const prefs = NORMALIZED_TIME_PREFS[time]

  for (const avoidNorm of prefs.avoidNorm) {
    if (ingNorm.includes(avoidNorm)) {
      return { appropriate: false, penalty: 3.0 }
    }
  }

  for (const preferredNorm of prefs.preferredNorm) {
    if (ingNorm.includes(preferredNorm)) {
      return { appropriate: true, penalty: -0.5 }
    }
  }

  return { appropriate: true, penalty: 0 }
}

/**
 * Get active class using pre-normalized ingredient
 */
export function getActiveClassNormalized(ingNorm: string): string | null {
  for (const { className, ingredientsNorm } of NORMALIZED_ACTIVE_CLASSES) {
    if (ingredientsNorm.some((i) => ingNorm.includes(i))) {
      return className
    }
  }
  return null
}
